import React, { useState } from "react";
import {
  Alert,
  Button,
  Dropdown,
  Modal,
  Space,
  Typography,
  Upload,
  message,
} from "antd";
import {
  DownloadOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  InboxOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const { Dragger } = Upload;
const { Text } = Typography;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const SUPPORTED_EXTENSIONS = ["csv", "xlsx"];

const fileExtension = (name = "") => name.split(".").pop().toLowerCase();

const responseError = async (response, fallback) => {
  try {
    const body = await response.json();
    return body?.detail || body?.error || fallback;
  } catch (error) {
    return fallback;
  }
};

const PayrollProfileBulkActions = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [queuedJob, setQueuedJob] = useState(null);

  const resetAndClose = () => {
    if (uploading) return;
    setOpen(false);
    setFileList([]);
    setQueuedJob(null);
  };

  const downloadTemplate = async (format) => {
    setDownloading(format);
    try {
      const endpoint =
        domainSettings.urls.finance.payrollProfileTemplate.replace(
          ":format",
          format,
        );
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${Utilities.getCookie("access")}` },
      });
      if (!response.ok) {
        message.error(
          await responseError(
            response,
            "Could not download the payroll profile template",
          ),
        );
        return;
      }

      const blobUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `payroll_profile_template.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(error);
      message.error("Could not download the payroll profile template");
    } finally {
      setDownloading(null);
    }
  };

  const selectFile = (file) => {
    if (!SUPPORTED_EXTENSIONS.includes(fileExtension(file.name))) {
      message.error("Only CSV and XLSX files are supported");
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_FILE_SIZE) {
      message.error("The selected file is larger than 10 MB");
      return Upload.LIST_IGNORE;
    }
    setFileList([file]);
    return false;
  };

  const uploadProfiles = async () => {
    const file = fileList[0]?.originFileObj || fileList[0];
    if (!file) {
      message.warning("Select a CSV or XLSX file to import");
      return;
    }

    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const response = await Utilities.fetchWithAuth(
        "POST",
        domainSettings.urls.finance.payrollProfilesUpload,
        body,
        navigate,
      );
      if (!response.ok) {
        Utilities.showError(response, "Could not upload the payroll profiles");
        return;
      }

      setQueuedJob(response.data);
      setFileList([]);
      message.success("Payroll profile import queued");
    } catch (error) {
      console.error(error);
      message.error("Could not upload the payroll profiles");
    } finally {
      setUploading(false);
    }
  };

  const downloadItems = [
    {
      key: "xlsx",
      icon: <FileExcelOutlined />,
      label: "Excel template (.xlsx)",
    },
    {
      key: "csv",
      icon: <FileTextOutlined />,
      label: "CSV template (.csv)",
    },
  ];

  return (
    <>
      <Space wrap>
        <Dropdown
          trigger={["click"]}
          menu={{
            items: downloadItems,
            onClick: ({ key }) => downloadTemplate(key),
          }}
          disabled={Boolean(downloading)}
        >
          <Button icon={<DownloadOutlined />} loading={Boolean(downloading)}>
            Download template
          </Button>
        </Dropdown>
        <Button
          icon={<UploadOutlined />}
          onClick={() => {
            setFileList([]);
            setQueuedJob(null);
            setOpen(true);
          }}
        >
          Import profiles
        </Button>
      </Space>

      <Modal
        title="Import employee payroll profiles"
        open={open}
        onCancel={resetAndClose}
        closable={!uploading}
        maskClosable={!uploading}
        footer={
          queuedJob ? (
            <Space>
              <Button onClick={resetAndClose}>Close</Button>
              <Button
                type="primary"
                onClick={() => navigate("/admin/finance/payroll/reports")}
              >
                View operation progress
              </Button>
            </Space>
          ) : (
            <Space>
              <Button onClick={resetAndClose} disabled={uploading}>
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                loading={uploading}
                onClick={uploadProfiles}
                disabled={!fileList.length}
              >
                Upload and import
              </Button>
            </Space>
          )
        }
      >
        {queuedJob ? (
          <Alert
            type="success"
            showIcon
            message="The import is processing in the background"
            description={
              <>
                You can safely leave this page. Track job{" "}
                <Text code>#{queuedJob.jobId}</Text> under Payroll Reports →
                Operations, then refresh this employee list after it completes.
              </>
            }
          />
        ) : (
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Alert
              type="info"
              showIcon
              message="Start with the template"
              description="Keep the column headings unchanged. Employees are matched by email, and profiles that already exist are skipped. If any new row is invalid, no new profiles from the file are saved."
            />
            <Dragger
              accept=".csv,.xlsx"
              maxCount={1}
              fileList={fileList}
              beforeUpload={selectFile}
              onRemove={() => {
                setFileList([]);
                return true;
              }}
              disabled={uploading}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag a payroll profile file here
              </p>
              <p className="ant-upload-hint">CSV or XLSX, up to 10 MB</p>
            </Dragger>
            <Text type="secondary">
              The import may also create salary assignments, bank history, and
              LST history when the corresponding template fields are supplied.
            </Text>
          </Space>
        )}
      </Modal>
    </>
  );
};

export default PayrollProfileBulkActions;
